import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword, signInWithPopup, signInWithRedirect, getRedirectResult } from '@firebase/auth';
import { auth, provider } from '../firebase';
import { updateUserProfile } from '../utils/secureFunctions';
import BrandLogo from '../components/BrandLogo';
import { useBrandingConfig } from '../utils/siteConfig';
import { getUserLocation, cacheLocation } from '../utils/geolocation';

export default function Signup() {
  const navigate = useNavigate();
  const { config } = useBrandingConfig('published');
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    email: '',
    password: ''
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    getRedirectResult(auth)
      .then((result) => { if (active && result?.user) navigate('/home'); })
      .catch(() => undefined);
    return () => { active = false; };
  }, [navigate]);

  const handleGoogle = async () => {
    setError(null);
    setLoading(true);
    try {
      // The backend creates the profile from the Google account on first sign-in.
      await signInWithPopup(auth, provider);
      navigate('/home');
    } catch (err: any) {
      const code = err?.code || '';
      if (['auth/popup-blocked', 'auth/cancelled-popup-request', 'auth/operation-not-supported-in-this-environment'].includes(code)) {
        try { await signInWithRedirect(auth, provider); return; } catch (redirErr: any) { setError(redirErr?.message || 'Google sign-up failed'); }
      } else if (code !== 'auth/popup-closed-by-user') {
        setError(err?.message || 'Google sign-up failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Detect user location in background
      const locationPromise = getUserLocation(false);

      // Create auth user
      await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      // Wait for location detection (with timeout)
      const location = await Promise.race([
        locationPromise,
        new Promise<null>(resolve => setTimeout(() => resolve(null), 3000))
      ]);

      // Cache location for future use
      if (location) {
        cacheLocation(location);
      }

      // Create initial user record with location
      await updateUserProfile({
        name: formData.name,
        username: formData.username,
        email: formData.email,
        detectedLocation: location ? {
          country: location.country,
          countryCode: location.countryCode,
          state: location.state,
          city: location.city,
          detectedAt: new Date().toISOString(),
        } : null,
      });

      navigate('/home');
    } catch (err: any) {
      setError(err.message || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.cardWrap}>
        <div style={styles.leftSection}>
          {config.logoUrl ? (
            <picture>
              <source srcSet={`${config.logoUrl}?w=160&fmt=webp 1x, ${config.logoUrl}?w=320&fmt=webp 2x`} type="image/webp" />
              <source srcSet={`${config.logoUrl}?w=160 1x, ${config.logoUrl}?w=320 2x`} />
              <img src={config.logoUrl} alt={config.brandName} style={{ height: 48, objectFit: 'contain', marginBottom: 8 }} loading="eager" fetchPriority="high" decoding="async" />
            </picture>
          ) : (
            <h1 style={styles.brand}><BrandLogo text={config.brandName || 'Belivah'} size={46} color="#fff" iconColor="#0E8F94" /></h1>
          )}
          <h2 style={styles.tagline}>{config.tagline}</h2>
          <p style={styles.welcome}>{config.signupWelcome}</p>
          <img src={config.authImageUrl || '/belivah-image.png'} alt="Belivah community" style={styles.image} loading="lazy" decoding="async" />
        </div>
        <div style={styles.formSection}>
          <h2 style={styles.formTitle}>Create your account</h2>
          {error && <div style={styles.error}>{error}</div>}
          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Name</label>
              <input
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                type="text"
                required
                style={styles.input}
                placeholder="Full name"
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Username</label>
              <input
                value={formData.username}
                onChange={e => setFormData({ ...formData, username: e.target.value })}
                type="text"
                required
                style={styles.input}
                placeholder="Choose a username"
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Email</label>
              <input
                value={formData.email}
                onChange={e => setFormData({ ...formData, email: e.target.value })}
                type="email"
                required
                style={styles.input}
                placeholder="Email address"
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Password</label>
              <input
                value={formData.password}
                onChange={e => setFormData({ ...formData, password: e.target.value })}
                type="password"
                required
                style={styles.input}
                placeholder="Create a password"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              style={styles.submitBtn}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 18px rgba(23,162,169,0.3)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(23,162,169,0.10)'; }}
              onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.98)'; }}
              onMouseUp={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
            >
              {loading ? 'Creating account...' : 'Sign Up'}
            </button>
          </form>
          <div style={styles.divider}><span style={styles.dividerLine} /><span>or</span><span style={styles.dividerLine} /></div>
          <button type="button" onClick={handleGoogle} disabled={loading} style={styles.googleBtn}>
            <span style={styles.googleIcon}>G</span> Continue with Google
          </button>
          <div style={styles.loginLink}>
            Already have an account? <a href="/login" style={styles.link}>Log in</a>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  page: {
    minHeight: '100vh',
    background: '#F5EDE1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'Inter, Arial, sans-serif',
  },
  cardWrap: {
    display: 'flex',
    background: '#fff',
    borderRadius: '24px',
    boxShadow: '0 4px 24px rgba(11,107,111,0.10)',
    overflow: 'hidden',
    maxWidth: '900px',
    width: '100%',
    minHeight: '520px',
    flexWrap: 'wrap',
  },
  leftSection: {
    flex: 1,
    background: '#0B6B6F',
    color: '#fff',
    padding: '48px 32px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    minWidth: '320px',
  },
  brand: {
    fontSize: '2.5rem',
    fontWeight: 800,
    marginBottom: '8px',
    letterSpacing: '2px',
  },
  tagline: {
    fontSize: '1.2rem',
    fontWeight: 500,
    color: '#66EAD1',
    marginBottom: '18px',
    letterSpacing: '1px',
  },
  welcome: {
    fontSize: '1.1rem',
    marginBottom: '24px',
    color: '#F5EDE1',
    fontWeight: 400,
  },
  image: {
    width: '100%',
    maxWidth: '350px',
    borderRadius: '16px',
    background: '#fff',
    objectFit: 'contain',
    boxShadow: '0 2px 8px rgba(102,234,209,0.10)',
    marginTop: 'auto',
  },
  formSection: {
    flex: 1,
    padding: '48px 32px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    minWidth: '320px',
  },
  formTitle: {
    fontSize: '2rem',
    color: '#0B6B6F',
    fontWeight: 700,
    marginBottom: '24px',
    textAlign: 'left',
  },
  error: {
    color: '#d32f2f',
    marginBottom: '16px',
    fontWeight: 500,
  },
  form: {
    width: '100%',
  },
  inputGroup: {
    marginBottom: '18px',
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontWeight: 500,
    color: '#072B2C',
    fontSize: '1rem',
  },
  input: {
    width: '100%',
    padding: '12px',
    borderRadius: '8px',
    border: '2px solid #17A2A9',
    fontSize: '1rem',
    fontFamily: 'inherit',
    marginBottom: '2px',
    outline: 'none',
    transition: 'box-shadow 0.2s, border-color 0.2s',
    boxSizing: 'border-box',
  },
  submitBtn: {
    width: '100%',
    padding: '14px',
    background: '#17A2A9',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1.1rem',
    fontWeight: 700,
    cursor: 'pointer',
    marginTop: '8px',
    boxShadow: '0 2px 8px rgba(23,162,169,0.10)',
    transition: 'background 0.2s, box-shadow 0.2s, transform 0.2s',
  },
  divider: { display: 'flex', alignItems: 'center', gap: 10, margin: '16px 0', color: '#7A8C8C', fontSize: '0.9rem' },
  dividerLine: { flex: 1, height: 1, background: '#E0E0E0' },
  googleBtn: { width: '100%', padding: '14px', background: '#fff', color: '#072B2C', border: '2px solid #E0E0E0', borderRadius: '8px', fontSize: '1rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' },
  googleIcon: { fontSize: '1.2rem', fontWeight: 700, color: '#17A2A9' },
  loginLink: {
    marginTop: '24px',
    textAlign: 'center',
    fontSize: '1rem',
    color: '#072B2C',
  },
  link: {
    color: '#17A2A9',
    textDecoration: 'none',
    fontWeight: 600,
    marginLeft: '4px',
  },
};
